import React from 'react';
import Button from '../common/Button';
import './PracticeTestCover.css';

/**
 * WAEC-style exam booklet cover template. Data is filled from subject, examType, duration, etc.
 * Mirrors the physical design of past papers for a "real exam" feel.
 */
const PracticeTestCover = ({
  subject,
  examType = 'WAEC',
  timeLimitMinutes,
  questionCount,
  paperLabel = '1',
  sessionLabel,
  candidateName,
  onStart,
  onBack
}) => {
  const examBodyName = examType.toUpperCase() === 'WAEC'
    ? 'THE WEST AFRICAN EXAMINATIONS COUNCIL'
    : `${examType.toUpperCase()} EXAMINATIONS`;
  const examTitle = examType.toUpperCase() === 'WAEC'
    ? 'West African Senior School Certificate Examination'
    : `${examType} Practice Examination`;
  const session = sessionLabel || `Practice · ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;
  const duration = timeLimitMinutes != null
    ? (timeLimitMinutes >= 60 ? `${Math.floor(timeLimitMinutes / 60)} ${timeLimitMinutes % 60 ? `½` : ''} hour${timeLimitMinutes > 60 ? 's' : ''}`.trim() : `${timeLimitMinutes} minutes`)
    : '—';

  return (
    <div className="practice-test-cover">
      <div className="cover-inner">
        {/* Top row: info block + paper ref + candidate placeholders */}
        <div className="cover-top-row">
          <div className="cover-info-block">
            <div className="cover-ref">Practice</div>
            <div className="cover-exam-name">{examType.toUpperCase()} Practice</div>
            <div className="cover-subject-paper">{String(subject || '').toUpperCase()} {paperLabel}</div>
            <div className="cover-duration">{duration}</div>
          </div>
          <div className="cover-paper-ref-large">{paperLabel}</div>
          <div className="cover-candidate-fields">
            <div className="cover-field">
              <span className="cover-field-label">Name:</span>
              <span className="cover-field-value">{candidateName || '....................................................'}</span>
            </div>
            <div className="cover-field">
              <span className="cover-field-label">Index Number:</span>
              <span className="cover-field-value">............................................</span>
            </div>
          </div>
        </div>

        {/* Examining body and exam title */}
        <div className="cover-body">
          <h1 className="cover-exam-body">{examBodyName}</h1>
          <h2 className="cover-exam-title">{examTitle}</h2>
        </div>

        {/* Subject, date, duration row */}
        <div className="cover-mid-row">
          <span className="cover-session">{session}</span>
          <span className="cover-subject-paper-mid">{String(subject || '').toUpperCase()} {paperLabel}</span>
          <span className="cover-duration-mid">{duration}</span>
        </div>

        {/* Instructions */}
        <div className="cover-instructions">
          <p className="cover-instructions-intro">
            Do not open this booklet until you are told to do so. While you are waiting, read the following instructions carefully.
          </p>
          <p>
            Write your <strong>name</strong> and <strong>index number</strong> in the spaces provided at the top right-hand corner of this booklet.
          </p>
          <p>
            This practice test consists of <strong>{questionCount ?? '—'} multiple choice questions</strong>. You have <strong>{duration}</strong> to complete the test. Answer all questions. Each question has four options (A–D); choose the best answer.
          </p>
          <p>
            Your answers will be marked automatically. You can flag questions for review and navigate between questions. Do not start until you are told to do so.
          </p>
        </div>

        {/* Footer */}
        <div className="cover-footer">
          <span className="cover-year">© {new Date().getFullYear()} PassAm Practice Test</span>
        </div>

        {/* Actions */}
        <div className="cover-actions">
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
          )}
          <Button type="button" variant="primary" onClick={onStart} size="lg">
            I have read the instructions. Start test
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PracticeTestCover;
